import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import axios from 'axios';
import {
   IoPersonOutline,
   IoWalletOutline,
   IoMailOutline,
   IoPeopleOutline,
   IoCashOutline,
   IoCalendarOutline,
   IoShieldCheckmarkOutline,
   IoStatsChartOutline,
   IoOpenOutline
} from 'react-icons/io5';
import LevelUserListModal from './LevelUserListModal';
import './TreeUserDetailModal.css'

export default function TreeUserDetailModal({ walletAddress, onClose, onViewFullProfile }) {
   const [stats, setStats] = useState(null);
   const [loading, setLoading] = useState(false);
   const [selectedLevel, setSelectedLevel] = useState(null);

   const environment = process.env.REACT_APP_ENVIRONMENT || 'development';
   const BASE_URL = environment === 'production'
      ? process.env.REACT_APP_PROD_API_URL
      : environment === 'staging'
         ? process.env.REACT_APP_STAG_API_URL
         : process.env.REACT_APP_DEV_API_URL || 'http://localhost:4001';

   useEffect(() => {
      if (walletAddress) {
         setLoading(true);
         axios.get(`${BASE_URL}/v1/validator-delegator/stats/${walletAddress}`, {
            headers: { "Authorization": `Bearer ${localStorage.getItem("acessToken")}` }
         })
            .then(res => setStats(res.data.data))
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
      }
   }, [walletAddress, BASE_URL]);

   if (!walletAddress) return null;

   const quickStat = (label, value, icon, colorClass) => (
      <div className="d-flex align-items-center gap-2 p-2 rounded-3 border bg-white shadow-sm flex-fill">
         <div className={`p-2 rounded-circle bg-opacity-10 ${colorClass.replace('text-', 'bg-')} ${colorClass} d-flex align-items-center justify-content-center`} style={{ width: '35px', height: '35px' }}>
            {icon}
         </div>
         <div>
            <div className="text-muted fw-bold text-uppercase" style={{ fontSize: '9px' }}>{label}</div>
            <div className={`fw-bold ${colorClass}`} style={{ fontSize: '13px' }}>{loading ? "..." : value}</div>
         </div>
      </div>
   );

   return (
      <>
         <Modal
            show={!!walletAddress}
            onHide={onClose}
            centered
            size="xl"
            contentClassName="tree-detail-modal-content border-0 shadow-lg overflow-hidden"
            style={{ '--bs-modal-width': '90vw' }}
         >
            <Modal.Header closeButton closeVariant="white" className="border-0 bg-primary text-white py-2 px-4 position-relative overflow-hidden" style={{ minHeight: '60px' }}>
               <div className="position-absolute top-0 start-0 w-100 h-100 opacity-25" style={{ background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.2) 0%, transparent 50%)' }} />
               <Modal.Title className="d-flex align-items-center gap-2 position-relative z-1">
                  <div className="bg-white p-1 rounded-circle text-primary d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>
                     <IoPersonOutline size={18} />
                  </div>
                  <div>
                     <div className="fs-6 fw-bold mb-0">{loading ? "Loading..." : stats?.identity?.username}</div>
                     <div className="x-small opacity-75 d-flex align-items-center gap-1 font-monospace" style={{ fontSize: '10px' }}>
                        <IoWalletOutline /> {walletAddress}
                     </div>
                  </div>
               </Modal.Title>
            </Modal.Header>

            <Modal.Body className="bg-light p-3 pb-0 overflow-x-hidden">
               {/* Top Quick Stats Row - More Compact */}
               <div className="row g-2 mb-2 px-1">
                  <div className="col-4">{quickStat("Validator No.", stats?.investment?.validatorStakes || 0, <IoShieldCheckmarkOutline size={18} />, "text-primary")}</div>
                  <div className="col-4">{quickStat("Delegator No.", stats?.investment?.delegatorStakes || 0, <IoPeopleOutline size={18} />, "text-info")}</div>
                  <div className="col-4">{quickStat("Total Purchase", `$${(stats?.investment?.totalUsdt || 0).toLocaleString()}`, <IoCashOutline size={18} />, "text-success")}</div>
               </div>

               <div className="row g-2 px-1">
                  {/* Profile Info */}
                  <div className="col-md-4">
                     <div className="bg-white p-2 px-3 rounded-3 border shadow-sm h-100">
                        <h6 className="fw-bold mb-2 d-flex align-items-center gap-2 text-secondary" style={{ fontSize: '13px' }}>
                           <IoStatsChartOutline /> Profile Overview
                        </h6>
                        <div className="d-flex flex-column gap-2">
                           <div className="d-flex align-items-center gap-2">
                              <IoMailOutline className="text-muted" size={16} />
                              <div>
                                 <div className="text-muted" style={{ fontSize: '10px' }}>Email Address</div>
                                 <div className="fw-bold small">{loading ? "..." : (stats?.identity?.email || "N/A")}</div>
                              </div>
                           </div>
                           <div className="d-flex align-items-center gap-2">
                              <IoCalendarOutline className="text-muted" size={16} />
                              <div>
                                 <div className="text-muted" style={{ fontSize: '10px' }}>Joined Date</div>
                                 <div className="fw-bold small">{loading ? "..." : (stats?.identity?.joinDate ? new Date(stats.identity.joinDate).toLocaleDateString() : "N/A")}</div>
                              </div>
                           </div>
                           <div className="d-flex align-items-center gap-2">
                              <IoPeopleOutline className="text-muted" size={16} />
                              <div>
                                 <div className="text-muted" style={{ fontSize: '10px' }}>Referrer</div>
                                 <div className="fw-bold small text-primary">{loading ? "..." : (stats?.referrer?.username || "Organic")}</div>
                              </div>
                           </div>
                        </div>
                        <div className="mt-3 pt-2 border-top">
                           <button
                              onClick={() => onViewFullProfile && onViewFullProfile(walletAddress)}
                              className="btn btn-outline-primary btn-sm w-100 d-flex align-items-center justify-content-center gap-2 rounded-pill"
                              style={{ fontSize: '12px' }}
                           >
                              <IoOpenOutline /> View Full Profile
                           </button>
                        </div>
                     </div>
                  </div>

                  {/* Network Positioning */}
                  <div className="col-md-8">
                     <div className="bg-white p-2 px-3 rounded-3 border shadow-sm h-100">
                        <h6 className="fw-bold mb-2 d-flex align-items-center gap-2 text-secondary" style={{ fontSize: '13px' }}>
                           <IoPeopleOutline /> User Network Positioning
                        </h6>
                        <div className="row g-2 mb-2">
                           <div className="col-4">
                              <div className="p-2 rounded-3 bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 text-center">
                                 <div className="fw-bold text-uppercase opacity-75" style={{ fontSize: '9px' }}>Current Level</div>
                                 <div className="fs-5 fw-bold">{loading ? "..." : (stats?.identity?.level !== undefined ? `Level ${stats.identity.level}` : "N/A")}</div>
                              </div>
                           </div>
                           <div className="col-4">
                              <div className="p-2 rounded-3 bg-info bg-opacity-10 text-info border border-info border-opacity-25 text-center">
                                 <div className="fw-bold text-uppercase opacity-75" style={{ fontSize: '9px' }}>Total Downline Depth</div>
                                 <div className="fs-5 fw-bold">{loading ? "..." : (stats?.network?.maxDepth || 0)} Levels</div>
                              </div>
                           </div>
                           <div className="col-4">
                              <div className="p-2 rounded-3 bg-success bg-opacity-10 text-success border border-success border-opacity-25 text-center">
                                 <div className="fw-bold text-uppercase opacity-75" style={{ fontSize: '9px' }}>Total Network Members</div>
                                 <div className="fs-5 fw-bold">{loading ? "..." : (stats?.network?.totalDownline || 0)}</div>
                              </div>
                           </div>
                           <div className="col-12">
                              <div className="p-2 rounded-3 bg-light border text-muted">
                                 <div className="fw-bold text-uppercase opacity-75 mb-1" style={{ fontSize: '8px' }}>Referral Path</div>
                                 <div className="font-monospace small text-truncate" style={{ fontSize: '10px' }}>{loading ? "..." : (stats?.identity?.path || "ROOT")}</div>
                              </div>
                           </div>
                        </div>

                        {/* Dynamic Level Buttons for Reference */}
                        <div className="d-flex flex-wrap gap-1 justify-content-start overflow-auto custom-scrollbar-mini py-1" style={{ maxHeight: '60px' }}>
                           {Object.keys(stats?.network?.downlineLevels || {}).sort((a, b) => a - b).map(lvl => (
                              <span
                                 key={lvl}
                                 className="px-2 py-1 rounded-pill border bg-light text-muted small flex-shrink-0"
                                 style={{ fontSize: '10px' }}
                              >
                                 Lvl {lvl} ({stats?.network?.downlineLevels[lvl] || 0})
                              </span>
                           ))}
                           {(!loading && Object.keys(stats?.network?.downlineLevels || {}).length === 0) && (
                              <div className="text-muted small italic" style={{ fontSize: '10px' }}>No downline levels found.</div>
                           )}
                        </div>
                     </div>
                  </div>

               </div>
            </Modal.Body>
         </Modal>

         <LevelUserListModal
            show={!!selectedLevel}
            onClose={() => setSelectedLevel(null)}
            walletAddress={walletAddress}
            level={selectedLevel}
         />
      </>
   );
}
